// src/components/common/ModerationWarning.jsx
// Inline red warning toast with "Request Exception" button
// Usage: <ModerationWarning moderation={moderationState} onDismiss={() => set(null)} />

import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

// Category labels for display
const CATEGORY_LABELS = {
  profanity:   'Profanity',
  politics:    'Political Content',
  religion:    'Religious Content',
  adult:       'Adult Content',
  hate_speech: 'Hate Speech',
  violence:    'Violent Content',
  self_harm:   'Self-Harm Content',
  illegal:     'Illegal Content',
  drugs:       'Drug-Related Content',
};

export default function ModerationWarning({ moderation, onDismiss }) {
  const [showExceptionForm, setShowExceptionForm] = useState(false);
  const [exceptionReason, setExceptionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  if (!moderation) return null;

  const categoryText = (moderation.categories || [])
    .map(c => CATEGORY_LABELS[c] || c)
    .join(', ');

  const handleSubmitException = async () => {
    if (!exceptionReason.trim()) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const res = await fetch(`${API_URL}/api/moderation/exception`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          moderationId: moderation.moderationId,
          reason: exceptionReason.trim(),
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setSubmitted(true);
      } else {
        setSubmitError(data.error || 'Failed to submit request');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-4 my-3 rounded-lg border-2 border-red-400 bg-red-50 shadow-lg overflow-hidden">
      {/* ─── Main warning bar ────────────────────────────────────────── */}
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        </div>

        {/* Message */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-800">Content Flagged</p>
          <p className="text-xs text-red-700 mt-0.5">
            This content was flagged for: <span className="font-medium">{categoryText}</span>.
            It will not be saved until reviewed.
          </p>
        </div>

        {/* Dismiss */}
        <button
          onClick={onDismiss}
          className="flex-shrink-0 text-red-400 hover:text-red-600 transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* ─── Request Exception button ────────────────────────────────── */}
      {!showExceptionForm && !submitted && (
        <div className="px-4 pb-3">
          <button
            onClick={() => setShowExceptionForm(true)}
            className="text-xs font-medium text-red-700 underline hover:text-red-900 transition"
          >
            Request an Exception
          </button>
        </div>
      )}

      {/* ─── Exception form ──────────────────────────────────────────── */}
      {showExceptionForm && !submitted && (
        <div className="px-4 pb-4 border-t border-red-200 pt-3">
          <p className="text-xs font-medium text-red-800 mb-2">
            Explain why this content should be allowed:
          </p>
          <textarea
            value={exceptionReason}
            onChange={e => setExceptionReason(e.target.value)}
            placeholder="Provide context for why this content is appropriate for a business platform..."
            className="w-full text-xs border border-red-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-red-400 resize-none"
            rows={3}
            maxLength={1000}
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleSubmitException}
              disabled={submitting || !exceptionReason.trim()}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
            <button
              onClick={() => { setShowExceptionForm(false); setExceptionReason(''); }}
              className="px-3 py-1.5 text-xs font-medium text-red-700 hover:text-red-900 transition"
            >
              Cancel
            </button>
          </div>
          {submitError && (
            <p className="text-xs text-red-600 mt-1">{submitError}</p>
          )}
        </div>
      )}

      {/* ─── Submitted confirmation ──────────────────────────────────── */}
      {submitted && (
        <div className="px-4 pb-3 border-t border-red-200 pt-3">
          <p className="text-xs text-green-700 font-medium">
            Exception request submitted. An administrator will review your content.
          </p>
        </div>
      )}
    </div>
  );
}
